import * as React from 'react';
import { useMemo } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Tooltip, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    Handle,
    Position,
    MarkerType,
    Node,
    Edge,
    NodeProps,
    useViewport,
} from '@xyflow/react';

// @ts-ignore
require('@xyflow/react/dist/style.css');

import { useTaskflowBuilder } from '../taskflowBuilderContext';
import { TASKFLOW_LANE_LABEL, Taskflow, TaskflowLane, TaskflowTask } from '../types';

interface DiagramPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    onAddTask?: () => void;
    onEditTask?: (taskId: string) => void;
}

const LANE_ORDER: TaskflowLane[] = ['records', 'fee', 'sec'];

const LANE_COLOR: Record<TaskflowLane, string> = {
    records: '#1565c0',
    fee: '#2e7d32',
    sec: '#e18713',
};

const ROW_HEIGHT = 190;
const COLUMN_WIDTH = 250;
const PADDING_X = 40;
const TASK_NODE_WIDTH = 196;
const TASK_NODE_HEIGHT = 70;
const GATE_NODE_SIZE = 180;
const ACTIVATION_EDGE_COLOR = '#565A72';
const SIDE_EFFECT_EDGE_COLOR = '#CBD0E3';

const describeDueDate = (task: TaskflowTask): string => {
    if (task.due.manual) {
        return 'Manual';
    }
    const sign = task.due.mode === 'add' ? '+' : '−';
    const unit = task.due.basis === 'work' ? ' working day' + (task.due.days === '1' ? '' : 's') : 'd';
    if (task.due.basis === 'work') {
        return `${sign}${task.due.days || '0'}${unit}`;
    }
    return task.due.days === '0' ? 'Same day' : `${sign}${task.due.days}${unit}`;
};

interface TaskNodeData {
    [key: string]: unknown;
    step: number;
    title: string;
    dueDisplay: string;
    lane: TaskflowLane;
}

interface GateNodeData {
    [key: string]: unknown;
    step: number;
    title: string;
    outcomeQuestion?: string;
}

const useNodeStyles = makeStyles((theme: Theme) =>
    createStyles({
        taskNode: {
            width: TASK_NODE_WIDTH,
            minHeight: TASK_NODE_HEIGHT,
            borderRadius: 10,
            border: `1.5px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            padding: theme.spacing(1, 1.5),
            position: 'relative',
            boxShadow: '0 1px 2px rgba(0,0,0,.06)',
            cursor: 'pointer',
        },
        taskAccent: {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
        },
        stepChip: {
            position: 'absolute',
            top: -12,
            left: -12,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: theme.palette.text.primary,
            color: theme.palette.background.paper,
            fontSize: 10.5,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        taskTitle: {
            fontSize: 13,
            fontWeight: 700,
            marginLeft: 6,
        },
        taskDue: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginLeft: 6,
            marginTop: 4,
            fontFamily: 'monospace',
        },
        gateNode: {
            width: GATE_NODE_SIZE,
            height: GATE_NODE_SIZE,
            background: '#fff6e9',
            border: '2px solid #e18713',
            boxShadow: '0 2px 6px rgba(35,37,59,.12)',
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            cursor: 'pointer',
        },
        gateStepChip: {
            position: 'absolute',
            top: 8,
            left: 8,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: theme.palette.text.primary,
            color: theme.palette.background.paper,
            fontSize: 10,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        gateTitle: {
            width: GATE_NODE_SIZE * 0.6,
            fontSize: 13,
            fontWeight: 800,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
        },
        gateQuestion: {
            width: GATE_NODE_SIZE * 0.55,
            fontSize: 10.5,
            color: theme.palette.text.secondary,
            marginTop: 5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        gateFlag: {
            fontSize: 9,
            fontWeight: 800,
            color: '#a15f04',
            marginTop: 5,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
        },
        laneBand: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            padding: theme.spacing(1, 1.5),
            fontFamily: 'monospace',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            color: theme.palette.text.disabled,
            textTransform: 'uppercase',
            boxSizing: 'border-box',
            borderTop: `1px solid ${theme.palette.divider}`,
        },
    })
);

const NODE_HANDLES = (
    <>
        <Handle type="target" position={Position.Left} isConnectable={false} style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Right} isConnectable={false} style={{ opacity: 0 }} />
    </>
);

const TaskNode: React.FC<NodeProps> = ({ data }) => {
    const classes = useNodeStyles();
    const nodeData = data as unknown as TaskNodeData;
    return (
        <div className={classes.taskNode}>
            {NODE_HANDLES}
            <div className={classes.taskAccent} style={{ background: LANE_COLOR[nodeData.lane] }} />
            <div className={classes.stepChip}>{nodeData.step}</div>
            <div className={classes.taskTitle}>{nodeData.title}</div>
            <div className={classes.taskDue}>{nodeData.dueDisplay}</div>
        </div>
    );
};

const GateNode: React.FC<NodeProps> = ({ data }) => {
    const classes = useNodeStyles();
    const nodeData = data as unknown as GateNodeData;
    const tooltipTitle = nodeData.outcomeQuestion ? `${nodeData.title} — ${nodeData.outcomeQuestion}` : nodeData.title;
    return (
        <Tooltip title={tooltipTitle} placement="top" arrow={true}>
            <div className={classes.gateNode}>
                {NODE_HANDLES}
                <div className={classes.gateStepChip}>{nodeData.step}</div>
                <div className={classes.gateTitle}>{nodeData.title}</div>
                {nodeData.outcomeQuestion ? <div className={classes.gateQuestion}>{nodeData.outcomeQuestion}</div> : null}
                <div className={classes.gateFlag}>Decision gate</div>
            </div>
        </Tooltip>
    );
};

const LaneBandNode: React.FC<NodeProps> = () => {
    const classes = useNodeStyles();
    return <div className={classes.laneBand} />;
};

const nodeTypes = { task: TaskNode, gate: GateNode, lane: LaneBandNode };

const useLaneLabelStyles = makeStyles((theme: Theme) =>
    createStyles({
        overlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 4,
        },
        chip: {
            position: 'absolute',
            left: theme.spacing(1.5),
            fontFamily: 'monospace',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            color: theme.palette.text.disabled,
            textTransform: 'uppercase',
            background: theme.palette.background.paper,
            padding: theme.spacing(0.4, 1.25),
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
        },
    })
);

const LaneLabelsOverlay: React.FC = () => {
    const classes = useLaneLabelStyles();
    const { y, zoom } = useViewport();
    return (
        <div className={classes.overlay}>
            {LANE_ORDER.map((lane, laneIndex) => (
                <span key={lane} className={classes.chip} style={{ top: laneIndex * ROW_HEIGHT * zoom + y + 12 }}>
                    {TASKFLOW_LANE_LABEL[lane]}
                </span>
            ))}
        </div>
    );
};

interface DiagramEdge extends Edge {
    pathOptions?: { borderRadius?: number; offset?: number };
}

interface DiagramGraph {
    nodes: Node[];
    edges: DiagramEdge[];
}

const buildDiagram = (taskflow: Taskflow): DiagramGraph => {
    const order = taskflow.taskOrder;
    const width = PADDING_X * 2 + Math.max(order.length, 1) * COLUMN_WIDTH;

    const laneNodes: Node[] = LANE_ORDER.map((lane, laneIndex) => ({
        id: `lane-${lane}`,
        type: 'lane',
        position: { x: 0, y: laneIndex * ROW_HEIGHT },
        data: { label: TASKFLOW_LANE_LABEL[lane] },
        style: { width, height: ROW_HEIGHT },
        draggable: false,
        selectable: false,
        connectable: false,
        zIndex: 0,
    }));

    const taskNodes: Node[] = order.map((taskId, idx) => {
        const task = taskflow.tasks[taskId];
        const laneIndex = LANE_ORDER.indexOf(task.lane);
        const x = PADDING_X + idx * COLUMN_WIDTH;
        const nodeHeight = task.isGate ? GATE_NODE_SIZE : TASK_NODE_HEIGHT;
        const y = laneIndex * ROW_HEIGHT + (ROW_HEIGHT - nodeHeight) / 2;

        if (task.isGate) {
            const data: GateNodeData = { step: idx + 1, title: task.title, outcomeQuestion: task.outcome?.question };
            return { id: taskId, type: 'gate', position: { x, y }, data, draggable: false, selectable: false, connectable: false, zIndex: 1 };
        }
        const data: TaskNodeData = { step: idx + 1, title: task.title, dueDisplay: describeDueDate(task), lane: task.lane };
        return { id: taskId, type: 'task', position: { x, y }, data, draggable: false, selectable: false, connectable: false, zIndex: 1 };
    });

    const edges: DiagramEdge[] = [];
    order.forEach((taskId, idx) => {
        const task = taskflow.tasks[taskId];
        if (task.onCompletionRules.length > 0) {
            task.onCompletionRules.forEach(rule => {
                if (!taskflow.tasks[rule.fromTaskId]) {
                    return;
                }
                const isActivation = rule.toStatus === 'Not Started';
                const color = isActivation ? ACTIVATION_EDGE_COLOR : SIDE_EFFECT_EDGE_COLOR;
                edges.push({
                    id: `edge-${taskId}-${rule.id}`,
                    source: taskId,
                    target: rule.fromTaskId,
                    type: 'smoothstep',
                    pathOptions: { borderRadius: 14, offset: 20 },
                    label: rule.ifOutcome ? `${rule.toStatus} · if ${rule.ifOutcome}` : rule.toStatus,
                    labelStyle: { fontSize: 11, fontWeight: 700 },
                    style: { stroke: color, strokeDasharray: isActivation ? undefined : '5 5' },
                    markerEnd: { type: MarkerType.ArrowClosed, color },
                    zIndex: 0,
                });
            });
        } else {
            const nextTaskId = order[idx + 1];
            if (nextTaskId) {
                edges.push({
                    id: `edge-${taskId}-next`,
                    source: taskId,
                    target: nextTaskId,
                    type: 'smoothstep',
                    pathOptions: { borderRadius: 14, offset: 20 },
                    style: { stroke: ACTIVATION_EDGE_COLOR },
                    markerEnd: { type: MarkerType.ArrowClosed, color: ACTIVATION_EDGE_COLOR },
                    zIndex: 0,
                });
            }
        }
    });

    return { nodes: [...laneNodes, ...taskNodes], edges };
};

const useLegendStyles = makeStyles((theme: Theme) =>
    createStyles({
        legend: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(2.5),
            marginTop: theme.spacing(1.5),
        },
        item: {
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 12,
            color: theme.palette.text.secondary,
            fontWeight: 600,
        },
        swatch: {
            width: 20,
            height: 2,
            background: ACTIVATION_EDGE_COLOR,
        },
        swatchDashed: {
            width: 20,
            height: 0,
            borderTop: `2px dashed ${SIDE_EFFECT_EDGE_COLOR}`,
        },
        swatchGate: {
            width: 12,
            height: 12,
            background: '#fff6e9',
            border: '2px solid #e18713',
            transform: 'rotate(45deg)',
        },
    })
);

const DiagramLegend: React.FC = () => {
    const classes = useLegendStyles();
    return (
        <Box className={classes.legend}>
            <span className={classes.item}><span className={classes.swatch} />Triggers next task</span>
            <span className={classes.item}><span className={classes.swatchDashed} />Status change / stand-down</span>
            <span className={classes.item}><span className={classes.swatchGate} />Decision gate</span>
        </Box>
    );
};

// ─── Colour token (matches ConfigureTaskDialog) ──────────────────────────────
const HEADER_BLUE = '#1565c0';

const useDialogChromeStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialogHeader: {
            background: HEADER_BLUE,
            color: '#fff',
            padding: theme.spacing(1.75, 2.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
        },
        dialogHeaderContent: {
            minWidth: 0,
        },
        dialogHeaderEyebrow: {
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
            color: '#fff',
            opacity: 0.9,
            marginBottom: 2,
        },
        dialogHeaderTitle: {
            fontWeight: 600,
            fontSize: 17,
            color: '#fff',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        dialogHeaderBadge: {
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 999,
            padding: theme.spacing(0.4, 1.25),
            fontSize: 11,
            fontWeight: 500,
            color: '#fff',
            background: 'rgba(255,255,255,0.1)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },
        footer: {
            borderTop: '1px solid #e0e0e0',
            padding: theme.spacing(1.75, 2.5),
            background: '#fff',
        },
    })
);

export const DiagramPreviewDialog: React.FC<DiagramPreviewDialogProps> = ({ open, onClose, onAddTask, onEditTask }) => {
    const classes = useDialogChromeStyles();
    const { activeTaskflow } = useTaskflowBuilder();

    const graph = useMemo(() => (activeTaskflow ? buildDiagram(activeTaskflow) : null), [activeTaskflow]);
    const hasTasks = !!activeTaskflow && activeTaskflow.taskOrder.length > 0;

    const onNodeClick = (_event: React.MouseEvent, node: Node) => {
        if (onEditTask && (node.type === 'task' || node.type === 'gate')) {
            onEditTask(node.id);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth={true}
            PaperProps={{ style: { height: '90vh' } }}
        >
            <Box className={classes.dialogHeader}>
                <Box className={classes.dialogHeaderContent}>
                    <Typography className={classes.dialogHeaderEyebrow}>Diagram preview</Typography>
                    <Typography className={classes.dialogHeaderTitle}>
                        {activeTaskflow ? activeTaskflow.name || 'Untitled taskflow' : 'Diagram preview'}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gridGap={12} flexShrink={0}>
                    {onAddTask && (
                        <Button onClick={onAddTask} variant="outlined" size="small" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>
                            Add task
                        </Button>
                    )}
                    {activeTaskflow && (
                        <Typography className={classes.dialogHeaderBadge}>
                            {activeTaskflow.status === 'published' ? 'Published' : 'Draft'}
                        </Typography>
                    )}
                </Box>
            </Box>
            <DialogContent dividers={true} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {hasTasks && graph ? (
                    <>
                        <Box style={{ width: '100%', flex: 1, minHeight: 0 }}>
                            <ReactFlow
                                nodes={graph.nodes}
                                edges={graph.edges}
                                nodeTypes={nodeTypes}
                                defaultViewport={{ x: 24, y: 24, zoom: 1 }}
                                minZoom={0.3}
                                maxZoom={1.5}
                                nodesDraggable={false}
                                nodesConnectable={false}
                                elementsSelectable={false}
                                zoomOnScroll={false}
                                onNodeClick={onNodeClick}
                                proOptions={{ hideAttribution: true }}
                            >
                                <MiniMap pannable={true} zoomable={true} />
                                <Controls showInteractive={false} />
                                <Background gap={24} />
                                <LaneLabelsOverlay />
                            </ReactFlow>
                        </Box>
                        <Box flexShrink={0}>
                            <DiagramLegend />
                        </Box>
                    </>
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={4}>
                        <Typography variant="body2" color="textSecondary">
                            Add at least one task to preview the swimlane diagram.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions className={classes.footer}>
                <Button onClick={onClose} variant="contained" color="primary">Close</Button>
            </DialogActions>
        </Dialog>
    );
};
